/**
 * Utilidades para manejar OAuth de Mercado Pago
 */

export interface MercadoPagoOAuthConfig {
  clientId: string;
  redirectUri: string;
}

export interface OAuthState {
  state: string;
  codeVerifier: string;
  projectData: {
    formData: any;
    fileItems: any[];
    selectedType: string;
    selectedSubCategory: string;
    selectedMaterialities: string[];
    selectedUses: string[];
  };
}

export interface PKCECodes {
  codeVerifier: string;
  codeChallenge: string;
  codeChallengeMethod: string;
}

const base64URLEncode = (str: string): string => {
  return str
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '');
};

const generateCodeVerifier = (): string => {
  const array = new Uint8Array(32);
  crypto.getRandomValues(array);
  const base64 = btoa(String.fromCharCode.apply(null, Array.from(array)));
  return base64URLEncode(base64);
};

const generateCodeChallenge = async (codeVerifier: string): Promise<string> => {
  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await crypto.subtle.digest('SHA-256', data);
  const base64 = btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array(digest))));
  return base64URLEncode(base64);
};

export const generatePKCECodes = async (): Promise<PKCECodes> => {
  const codeVerifier = generateCodeVerifier();
  const codeChallenge = await generateCodeChallenge(codeVerifier);
  
  return {
    codeVerifier,
    codeChallenge,
    codeChallengeMethod: 'S256'
  };
};

export const generateOAuthState = (): string => {
  return Math.random().toString(36).substring(2, 15) + 
         Math.random().toString(36).substring(2, 15);
};

const DB_NAME = 'edify_oauth_storage';
const DB_VERSION = 1;
const FILE_STORE = 'files';

const openIndexedDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(FILE_STORE)) {
        db.createObjectStore(FILE_STORE);
      }
    };
  });
};

const saveFilesToIndexedDB = async (fileItems: any[]): Promise<void> => {
  const db = await openIndexedDB();

  for (const item of fileItems) {
    if (item.type !== 'file' || !item.file) continue;

    const arrayBuffer = await item.file.arrayBuffer();
    const fileData = {
      arrayBuffer,
      name: item.file.name,
      type: item.file.type,
      lastModified: item.file.lastModified
    };

    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(FILE_STORE, 'readwrite');
      const store = tx.objectStore(FILE_STORE);
      const request = store.put(fileData, item.id);
      request.onerror = () => reject(request.error);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  }
};

const loadFilesFromIndexedDB = async (fileItemsMetadata: any[]): Promise<any[]> => {
  const db = await openIndexedDB();

  const result = await Promise.all(
    fileItemsMetadata.map(async (item) => {
      if (item.isFile && item.type === 'file') {
        const fileData = await new Promise<any>((resolve, reject) => {
          const tx = db.transaction(FILE_STORE, 'readonly');
          const store = tx.objectStore(FILE_STORE);
          const request = store.get(item.id);
          request.onerror = () => reject(request.error);
          tx.oncomplete = () => {
            const data = request.result;
            if (data) resolve(data);
            else reject(new Error(`File ${item.id} not found in IndexedDB`));
          };
          tx.onerror = () => reject(tx.error);
          tx.onabort = () => reject(tx.error);
        });

        let file: File;
        if (fileData instanceof File) {
          file = fileData;
        } else if (fileData.arrayBuffer) {
          const blob = new Blob([fileData.arrayBuffer], { type: fileData.type || 'application/octet-stream' });
          file = new File([blob], fileData.name || item.name || 'file', {
            type: fileData.type || 'application/octet-stream',
            lastModified: fileData.lastModified || Date.now()
          });
        } else {
          throw new Error(`Invalid file data format for ${item.id}`);
        }

        return {
          id: item.id,
          type: 'file',
          file: file,
          preview: item.preview,
          tagId: item.tagId,
          name: item.name
        };
      }
      return {
        id: item.id,
        type: item.type,
        link: item.link,
        tagId: item.tagId,
        name: item.name
      };
    })
  );

  return result;
};

const clearFilesFromIndexedDB = async (): Promise<void> => {
  const db = await openIndexedDB();
  const transaction = db.transaction(FILE_STORE, 'readwrite');
  const store = transaction.objectStore(FILE_STORE);
  store.clear();
};

const serializeFileItems = (fileItems: any[]): any[] => {
  return fileItems.map(item => {
    if (item.type === 'file' && item.file) {
      return {
        id: item.id,
        type: 'file',
        fileName: item.file.name,
        fileType: item.file.type,
        fileSize: item.file.size,
        preview: item.preview,
        tagId: item.tagId,
        name: item.name,
        isFile: true
      };
    }
    return {
      id: item.id,
      type: item.type,
      link: item.link,
      tagId: item.tagId,
      name: item.name,
      isFile: false
    };
  });
};

/**
 * Genera la URL de autorización de Mercado Pago
 */
export const generateMercadoPagoAuthUrl = (
  config: MercadoPagoOAuthConfig, 
  state: string,
  codeChallenge: string,
  codeChallengeMethod: string
): string => {
  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: 'code',
    platform_id: 'mp',
    state: state,
    redirect_uri: config.redirectUri,
    code_challenge: codeChallenge,
    code_challenge_method: codeChallengeMethod
  });
  
  return `https://auth.mercadopago.com/authorization?${params.toString()}`;
};

/**
 * Inicia el flujo OAuth de Mercado Pago
 */
export const initiateMercadoPagoOAuth = async (
  config: MercadoPagoOAuthConfig,
  formData: any,
  fileItems: any[],
  selectedType: string,
  selectedSubCategory: string,
  selectedMaterialities: string[],
  selectedUses: string[]
): Promise<void> => {
  const pkceCodes = await generatePKCECodes();
  const state = generateOAuthState();
  
  await saveFilesToIndexedDB(fileItems);
  const serializedFileItems = serializeFileItems(fileItems);
  
  localStorage.setItem('mp_oauth_state', state);
  localStorage.setItem('mp_oauth_code_verifier', pkceCodes.codeVerifier);
  localStorage.setItem('project_data', JSON.stringify({
    formData,
    fileItems: serializedFileItems,
    selectedType,
    selectedSubCategory,
    selectedMaterialities,
    selectedUses
  }));
  
  const authUrl = generateMercadoPagoAuthUrl(config, state, pkceCodes.codeChallenge, pkceCodes.codeChallengeMethod);
  
  window.location.href = authUrl;
};

/**
 * Valida el state de OAuth para seguridad
 */
export const validateOAuthState = (receivedState: string): boolean => {
  const storedState = localStorage.getItem('mp_oauth_state');
  return storedState === receivedState;
};

/**
 * Limpia los datos temporales de OAuth
 */
export const cleanupOAuthData = async (): Promise<void> => {
  localStorage.removeItem('mp_oauth_state');
  localStorage.removeItem('mp_oauth_code_verifier');
  localStorage.removeItem('project_data');
  await clearFilesFromIndexedDB();
};

/**
 * Obtiene los datos del proyecto guardados para OAuth
 */
export const getProjectDataFromOAuth = async (): Promise<{ 
  formData: any; 
  fileItems: any[]; 
  selectedType: string; 
  selectedSubCategory: string; 
  selectedMaterialities: string[]; 
  selectedUses: string[] 
} | null> => {
  const projectData = localStorage.getItem('project_data');
  if (!projectData) return null;
  
  try {
    const parsed = JSON.parse(projectData);
    const fileItemsWithFiles = await loadFilesFromIndexedDB(parsed.fileItems);
    
    return {
      ...parsed,
      fileItems: fileItemsWithFiles
    };
  } catch (error) {
    console.error('Error parsing project data:', error);
    return null;
  }
};

/**
 * Obtiene el code_verifier guardado para OAuth
 */
export const getCodeVerifierFromOAuth = (): string | null => {
  return localStorage.getItem('mp_oauth_code_verifier');
};

/**
 * Verifica si el usuario ya tiene autorización de Mercado Pago
 */
export const checkUserMercadoPagoAuth = (userData: any): boolean => {
  if (!userData) return false;
  
  const merchantAccount = userData.merchantAccount;
  
  if (!merchantAccount) return false;
  
  if (merchantAccount.deleted_at !== null) return false;
  
  return true;
};

/**
 * Determina si debe mostrar el modal de autorización de Mercado Pago
 */
export const shouldShowMercadoPagoAuth = (userData: any): boolean => {
  return !checkUserMercadoPagoAuth(userData);
};
